import type { DTechFileConfig, DTechDetectionResult, DTechDetectionOptions } from '../types/DTechFileConfig';
import { getAllConsolidatedDTechConfigs, LEGACY_CONFIGS } from '../configs';

/**
 * D-Tech file type detection utility
 * 
 * Clean, simple signature-based detection for consolidated D-Tech configs.
 * No more version preferences or ambiguity resolution - the consolidated
 * configs eliminate artificial ambiguities.
 */
export class DTechFileDetector {
  
  /**
   * Detect D-Tech file type from CSV content
   * @param content CSV file content as string
   * @param options Detection options
   * @returns Detection result with config and confidence, or null if no match
   */
  static detectFileType(content: string, options: DTechDetectionOptions = {}): DTechDetectionResult {
    const {
      minConfidence = 0.7,
      includeLegacy = true
    } = options;

    try {
      // Basic validation
      if (!content || !content.trim()) {
        return this.noMatchResult();
      }

      // Extract header line
      const lines = content.split('\n');
      if (lines.length < 1) {
        return this.noMatchResult();
      }

      const headerLine = lines[0];
      if (!headerLine || !headerLine.trim()) {
        return this.noMatchResult();
      }

      // Parse available columns from header
      const availableColumns = this.parseColumns(headerLine);
      if (availableColumns.length === 0) {
        return this.noMatchResult();
      }

      // Get all available configs
      const configs: DTechFileConfig[] = [
        ...getAllConsolidatedDTechConfigs(),
        ...(includeLegacy ? Object.values(LEGACY_CONFIGS) as DTechFileConfig[] : [])
      ];

      // Score all configs against the available columns
      const scoredConfigs = configs.map(config => ({
        config,
        ...this.scoreConfigMatch(availableColumns, config)
      }));

      // Find the best match above minimum confidence
      const viableConfigs = scoredConfigs.filter(scored => scored.confidence >= minConfidence);
      
      if (viableConfigs.length === 0) {
        return this.noMatchResult();
      }

      // Sort by confidence (highest first)
      viableConfigs.sort((a, b) => b.confidence - a.confidence);
      
      const bestMatch = viableConfigs[0];
      
      // Check for multiple high-confidence matches (shouldn't happen with consolidated configs)
      const topMatches = viableConfigs.filter(config => 
        Math.abs(config.confidence - bestMatch.confidence) < 0.01
      );
      
      if (topMatches.length > 1) {
        console.warn(`Multiple D-Tech configs match with similar confidence. This shouldn't happen with consolidated configs.`, 
          topMatches.map(m => `${m.config.fileType} (${(m.confidence * 100).toFixed(1)}%)`));
      }

      return {
        config: bestMatch.config,
        confidence: bestMatch.confidence,
        matchedColumns: bestMatch.matchedColumns,
        method: 'signature'
      };

    } catch (error) {
      console.error('D-Tech file detection error:', error);
      return this.noMatchResult();
    }
  }



  /**
   * Parse column names from header line
   * Handles various CSV formats and delimiters
   */
  private static parseColumns(headerLine: string): string[] {
    // Try comma separation first
    let columns = headerLine.split(',').map(col => col.trim().replace(/['"]/g, ''));
    
    // If we only get one column, try tab separation
    if (columns.length === 1) {
      columns = headerLine.split('\t').map(col => col.trim().replace(/['"]/g, ''));
    }
    
    // Filter out empty columns
    return columns.filter(col => col.length > 0);
  }

  /**
   * Score how well a config matches the available columns
   * Returns confidence score and matched columns
   */
  private static scoreConfigMatch(
    availableColumns: string[], 
    config: DTechFileConfig
  ): { confidence: number; matchedColumns: string[] } {
    const signatureColumns = config.signatureColumns || [];
    const signatureThreshold = config.signatureThreshold || Math.ceil(signatureColumns.length * 0.7);
    
    if (signatureColumns.length === 0) {
      return { confidence: 0, matchedColumns: [] };
    }

    // Find signature columns that match (case-insensitive, trimmed)
    const matchedColumns: string[] = [];
    
    for (const sigCol of signatureColumns) {
      const match = availableColumns.find(availCol => 
        availCol.toLowerCase().trim() === sigCol.toLowerCase().trim()
      );
      if (match) {
        matchedColumns.push(sigCol);
      }
    }

    // Check if we meet the signature threshold
    if (matchedColumns.length < signatureThreshold) {
      return { confidence: 0, matchedColumns };
    }

    // Calculate confidence based on signature match quality
    const signatureRatio = matchedColumns.length / signatureColumns.length;
    let confidence = signatureRatio * 0.8; // Base score from signature match
    
    // Bonus for time column presence
    const timeColumns = config.timeColumns || [];
    const hasTimeColumn = timeColumns.some(timeCol =>
      availableColumns.some(availCol => 
        availCol.toLowerCase().trim() === timeCol.toLowerCase().trim()
      )
    );
    
    if (hasTimeColumn) {
      confidence += 0.15;
    }
    
    // Small bonus for perfect signature match
    if (signatureRatio === 1.0) {
      confidence += 0.05;
    }
    
    // Cap at 1.0
    confidence = Math.min(confidence, 1.0);
    
    return { confidence, matchedColumns };
  }

  /**
   * Return a standard "no match" result
   */
  private static noMatchResult(): DTechDetectionResult {
    return {
      config: null,
      confidence: 0,
      matchedColumns: [],
      method: 'signature'
    };
  }
}